import { useState, useEffect } from 'react';
import { useStore } from '@/lib/store';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, X, BarChart3, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';

// Industry standard: persist cookie consent in its own localStorage key
// so it survives logout (which clears the main store/IndexedDB).
const COOKIE_CONSENT_KEY = 'ae-cookie-consent';

function loadPersistedConsent(): { essential: boolean; analytics: boolean; dismissed: boolean } | null {
    try {
        const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return null;
}

function persistConsent(consent: { essential: boolean; analytics: boolean; dismissed: boolean }) {
    try {
        localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    } catch { /* ignore */ }
}

export function CookieConsent() {
    const { consent, setConsent, showCookiePrefs, setShowCookiePrefs } = useStore();
    const [isVisible, setIsVisible] = useState(false);

    // On mount, restore consent from dedicated localStorage key (survives logout)
    useEffect(() => {
        const persisted = loadPersistedConsent();
        if (persisted && persisted.dismissed && !consent.dismissed) {
            setConsent(persisted);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // Show after a short delay if not yet dismissed
        if (!consent.dismissed) {
            const timer = setTimeout(() => setIsVisible(true), 2000);
            return () => clearTimeout(timer);
        }
    }, [consent.dismissed]);

    const handleAcceptAll = () => {
        const newConsent = { essential: true, analytics: true, dismissed: true };
        setConsent(newConsent);
        persistConsent(newConsent);
        setIsVisible(false);
    };

    const handleAcceptEssential = () => {
        const newConsent = { essential: true, analytics: false, dismissed: true };
        setConsent(newConsent);
        persistConsent(newConsent);
        setIsVisible(false);
    };

    if (!isVisible && !showCookiePrefs) return null;

    return (
        <AnimatePresence>
            {isVisible && !showCookiePrefs && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    className="fixed bottom-6 left-6 right-6 md:left-auto md:right-8 md:w-[400px] z-[100]"
                >
                    <div className="relative overflow-hidden rounded-3xl border border-white/20 bg-slate-900/80 backdrop-blur-xl shadow-2xl p-6">
                        {/* Gradient flare */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

                        <div className="relative z-10">
                            <div className="flex items-start gap-4 mb-4">
                                <div className="p-2.5 rounded-2xl bg-indigo-500/20 border border-indigo-400/30">
                                    <ShieldCheck className="w-6 h-6 text-indigo-400" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-lg font-bold text-white mb-1">Privacy & Engine Access</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed">
                                        This free tool requires essential storage to build your plan.
                                        By continuing, you help us keep the engine running smoothly.
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <Button
                                    onClick={handleAcceptAll}
                                    className="w-full h-12 bg-white text-slate-900 hover:bg-slate-100 rounded-2xl font-bold transition-all active:scale-95"
                                >
                                    Accept & Continue
                                </Button>
                                <div className="flex gap-2">
                                    <Button
                                        variant="ghost"
                                        onClick={() => setShowCookiePrefs(true)}
                                        className="flex-1 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl text-xs h-10"
                                    >
                                        Preferences
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        onClick={handleAcceptEssential}
                                        className="flex-1 text-slate-400 hover:text-white hover:bg-white/5 rounded-xl text-xs h-10"
                                    >
                                        Essential Only
                                    </Button>
                                </div>
                            </div>

                            <p className="mt-4 text-[10px] text-slate-500 text-center">
                                Read our <Link to="/privacy" className="text-indigo-400 hover:underline">Privacy Policy</Link> for full details.
                            </p>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Detailed Preferences Modal */}
            {showCookiePrefs && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-slate-900 border border-white/10 rounded-[32px] w-full max-w-lg overflow-hidden shadow-2xl"
                    >
                        <div className="p-8">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-2xl font-bold text-white">Trust Center</h2>
                                <button onClick={() => setShowCookiePrefs(false)} className="p-2 text-slate-500 hover:text-white transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-6">
                                <div className="flex items-start gap-4 p-4 rounded-2xl bg-white/5 border border-white/5">
                                    <Lock className="w-5 h-5 text-indigo-400 mt-1" />
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <h4 className="font-bold text-white">Strictly Necessary</h4>
                                            <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full uppercase">Always On</span>
                                        </div>
                                        <p className="text-sm text-slate-400">Required for the training scheduler, plan generation, and login. The app won't work without these.</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 transition-all hover:bg-white/10">
                                    <BarChart3 className="w-5 h-5 text-emerald-400 mt-1" />
                                    <div className="flex-1">
                                        <div className="flex justify-between items-center mb-1">
                                            <h4 className="font-bold text-white">Analytics (PostHog)</h4>
                                            <div
                                                onClick={() => {
                                                    const newAnalytics = !consent.analytics;
                                                    setConsent({ analytics: newAnalytics });
                                                    persistConsent({ ...consent, analytics: newAnalytics });
                                                }}
                                                className={cn(
                                                    "w-10 h-5 rounded-full relative transition-colors cursor-pointer",
                                                    consent.analytics ? "bg-emerald-500" : "bg-slate-700"
                                                )}
                                            >
                                                <div className={cn(
                                                    "absolute top-1 left-1 w-3 h-3 bg-white rounded-full transition-all",
                                                    consent.analytics ? "translate-x-5" : "translate-x-0"
                                                )} />
                                            </div>
                                        </div>
                                        <p className="text-sm text-slate-400">Helps us understand how athletes use the tool so we can improve the training experience.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex gap-3">
                                <Button
                                    onClick={() => {
                                        const newConsent = { ...consent, dismissed: true };
                                        setConsent({ dismissed: true });
                                        persistConsent(newConsent);
                                        setShowCookiePrefs(false);
                                        setIsVisible(false);
                                    }}
                                    className="flex-1 h-12 bg-white text-slate-900 hover:bg-slate-100 rounded-2xl font-bold"
                                >
                                    Save Choices
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
